import { useCallback, useEffect, useRef, useState } from 'react';
import { API } from 'aws-amplify';
import exportConfig from '../config';
import {
	getCurrentExportRows,
	getExportRows,
	getExportState,
	isExportSettled,
	STEEL_EXPORT_PERIODS
} from '../helpers/exportHelpers';

const FULL_EXPORT_REFRESH_INTERVAL = 60000;
const SELECTED_SITES_REFRESH_INTERVAL = 15000;
const REQUEST_TIMEOUT = 30000;

const requestExport = async (path, method, body, queryParams) => {
	let timeout;
	try {
		const request = API[method](exportConfig.gainsApi.NAME, path, {
			authMode: 'AMAZON_COGNITO_USER_POOLS',
			headers: { 'Content-Type': 'application/json' },
			...(body ? { body } : {}),
			...(queryParams ? { queryStringParameters: queryParams } : {})
		});
		return await Promise.race([
			request,
			new Promise((_, reject) => {
				timeout = setTimeout(() => reject(new Error('Request timed out.')), REQUEST_TIMEOUT);
			})
		]);
	} finally {
		clearTimeout(timeout);
	}
};

const getErrorMessage = (error) => error?.response?.data?.message ||
	(typeof error?.response?.data === 'string' ? error.response.data : error?.message) ||
	'An unknown error occurred.';

const readPending = (key) => {
	try {
		return JSON.parse(sessionStorage.getItem(key)) || null;
	} catch {
		return null;
	}
};

const savePending = (key, pending) => {
	try {
		if (pending) sessionStorage.setItem(key, JSON.stringify(pending));
		else sessionStorage.removeItem(key);
	} catch {
		// Status checking still works when browser storage is unavailable.
	}
};

// Mount a separate instance for each commodity/type so late responses cannot
// overwrite another selection. Remember pending requests when returning to it.
export default function useDataExport(commodity, exportType) {
	const storageKey = `qc-export:${exportConfig.gainsEnvironment}:${exportType}:${commodity}`;
	const [pending, setPending] = useState(() => readPending(storageKey));
	const [response, setResponse] = useState(null);
	const [loading, setLoading] = useState(false);
	const [starting, setStarting] = useState(false);
	const [statusError, setStatusError] = useState(null);
	const [startError, setStartError] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const active = useRef(false);
	const refreshing = useRef(false);
	const startingRequest = useRef(false);
	const pendingRequest = useRef(pending);
	const path = `/export/${exportType}/${commodity}`;

	const updatePending = useCallback((value) => {
		pendingRequest.current = value;
		savePending(storageKey, value);
		if (active.current) setPending(value);
	}, [storageKey]);

	const refreshStatus = useCallback(async () => {
		if (refreshing.current) return;
		refreshing.current = true;
		if (active.current) setLoading(true);
		try {
			const periods = commodity === 'steel' && exportType === 'agg' ? STEEL_EXPORT_PERIODS : [];
			const [latest, ...periodResults] = await Promise.all([
				requestExport(`${path}/status`, 'get'),
				...periods.map((period) => requestExport(`${path}/status`, 'get', null, { period }))
			]);
			const result = {
				...latest,
				periods: Object.fromEntries(periods.map((period, index) => [period, periodResults[index].status]))
			};
			if (active.current) {
				setResponse(result);
				setStatusError(null);
				setUpdatedAt(new Date().toLocaleTimeString());
				const rows = getCurrentExportRows(
					getExportRows(result, commodity, exportType), pendingRequest.current, exportType
				);
				if (pendingRequest.current && isExportSettled(rows)) updatePending(null);
			}
			return result;
		} catch (error) {
			if (active.current) setStatusError(getErrorMessage(error));
		} finally {
			refreshing.current = false;
			if (active.current) setLoading(false);
		}
	}, [path, commodity, exportType, updatePending]);

	useEffect(() => {
		active.current = true;
		refreshStatus();
		return () => { active.current = false; };
	}, [refreshStatus]);

	const rows = getCurrentExportRows(
		getExportRows(response, commodity, exportType), pending, exportType
	);
	const state = getExportState(rows, Boolean(pending));
	const running = state === 'RUNNING';
	const refreshInterval = exportType === 'site' && pending?.refreshInterval === SELECTED_SITES_REFRESH_INTERVAL
		? SELECTED_SITES_REFRESH_INTERVAL : FULL_EXPORT_REFRESH_INTERVAL;

	useEffect(() => {
		if (starting || state === 'FAILED' || (!pending && !running) || statusError) return;
		const interval = setInterval(refreshStatus, refreshInterval);
		return () => clearInterval(interval);
	}, [pending, running, starting, state, statusError, refreshStatus, refreshInterval]);

	const startExport = async (body) => {
		if (startingRequest.current || refreshing.current || (pendingRequest.current && state !== 'FAILED')) return;
		startingRequest.current = true;
		setStarting(true);
		setStartError(null);
		try {
			// Recheck before starting to avoid exporting over an already running job.
			const latest = await refreshStatus();
			if (!active.current) return;
			if (!latest) {
				setStartError('Could not check the current export status. Refresh status before trying again.');
				return;
			}
			const latestRows = getExportRows(latest, commodity, exportType);
			if (latestRows.some(({ status }) => status?.state === 'RUNNING')) {
				setStartError('An export is already running for this commodity and export type.');
				return;
			}
			const request = {
				requestedAt: Date.now(),
				refreshInterval: exportType === 'site' && body.ids?.length
					? SELECTED_SITES_REFRESH_INTERVAL : FULL_EXPORT_REFRESH_INTERVAL,
				baseline: Object.fromEntries(latestRows.map(({ key, status }) => [key, status]))
			};
			updatePending(request);
			await requestExport(path, 'post', body);
			if (active.current) await refreshStatus();
		} catch (error) {
			updatePending(null);
			if (active.current) {
				setResponse(null);
				setStartError(`Could not confirm that the export started. ${getErrorMessage(error)} Refresh status before retrying.`);
			}
		} finally {
			startingRequest.current = false;
			if (active.current) setStarting(false);
		}
	};

	return {
		rows, state, pending, loading, starting, statusError, startError,
		updatedAt, refreshStatus, startExport, refreshInterval
	};
}
